/**
 * Client Link Error Helper (SO-P274, E4.b, fix1)
 *
 * Maps errors by structured error codes rather than fragile message substrings.
 * - Firebase Auth errors by code ('auth/invalid-verification-code', 'auth/code-expired', 'auth/too-many-requests')
 * - Functions callable errors by code and details.reason
 */

export type ClientLinkErrorKind =
  | 'WRONG_OTP'
  | 'OTP_EXPIRED'
  | 'PHONE_MISMATCH'
  | 'SUBJECT_CLOSED'
  | 'NOT_FOUND'
  | 'EXPIRED'
  | 'REVOKED'
  | 'RESOURCE_EXHAUSTED'
  | 'OTHER'

export interface ParsedErrorLike {
  code?: string
  message?: string
  details?: {
    reason?: string
  }
}

export function classifyClientLinkError(err: unknown): ClientLinkErrorKind {
  if (!err || typeof err !== 'object') {
    return 'OTHER'
  }

  const errorObj = err as ParsedErrorLike

  // 1. Structured details.reason (callable refusal reasons like SUBJECT_CLOSED)
  const reason = typeof errorObj.details?.reason === 'string' ? errorObj.details.reason : ''
  if (reason === 'SUBJECT_CLOSED') return 'SUBJECT_CLOSED'
  if (reason === 'EXPIRED') return 'EXPIRED'
  if (reason === 'REVOKED') return 'REVOKED'

  // 2. Structured code
  const code = typeof errorObj.code === 'string' ? errorObj.code.toLowerCase().trim() : ''

  // Firebase Auth error codes
  if (code === 'auth/invalid-verification-code') return 'WRONG_OTP'
  if (code === 'auth/code-expired') return 'OTP_EXPIRED'
  if (code === 'auth/too-many-requests') return 'RESOURCE_EXHAUSTED'

  // Firebase Functions / Callable error codes
  if (code === 'resource-exhausted' || code === 'functions/resource-exhausted') {
    return 'RESOURCE_EXHAUSTED'
  }
  if (code === 'permission-denied' || code === 'functions/permission-denied') {
    return 'PHONE_MISMATCH'
  }
  if (code === 'not-found' || code === 'functions/not-found') {
    return 'NOT_FOUND'
  }

  // 3. Fallback for tests passing exact reason string in message
  const msg = typeof errorObj.message === 'string' ? errorObj.message.trim() : ''
  if (msg === 'SUBJECT_CLOSED') return 'SUBJECT_CLOSED'
  if (msg === 'EXPIRED') return 'EXPIRED'
  if (msg === 'REVOKED') return 'REVOKED'

  return 'OTHER'
}
